'use client';

import { KeyboardEvent, ReactNode, useId, useRef, useState } from 'react';

import { css } from 'styled-system/css';

type Industry = { name: string; agents: { name: string; icon: ReactNode }[] };

type Props = {
  /** Server-rendered heading + subtitle, on top of the left column. */
  intro: ReactNode;
  industries: Industry[];
  /** One server-rendered card per agent, by industry. */
  cards: ReactNode[][];
  industriesLabel: string;
  /** Small label over the agents' list ("Pick an agent"). */
  pickLabel: string;
};

const EASE = 'cubic-bezier(.22,.61,.36,1)';

const rootClass = css({
  display: 'grid',
  gridTemplateColumns: { base: 'minmax(0, 1fr)', lg: 'minmax(0, 5fr) minmax(0, 7fr)' },
  columnGap: { lg: 16, xl: 24 },
  rowGap: 10,
  alignItems: 'start',
});

// Neutral controls, so they read as navigation and not as calls to action: the industries are text
// tabs on a hairline (the open one dark, underlined), the agents a list of rows with a chevron
// (the open one dark, with a dark edge). Phones: the industry tabs scroll sideways.
const LINE = 'rgba(6, 3, 15, 0.12)';
const scrollerClass = css({
  marginTop: { base: 8, md: 10 },
  marginX: { base: -5, sm: 0 },
  paddingX: { base: 5, sm: 0 },
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});
const pillsClass = css({
  display: 'flex',
  gap: { base: 5, md: 7 },
  minWidth: 'max-content',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: LINE,
});
const pillClass = css({
  appearance: 'none',
  position: 'relative',
  flex: 'none',
  minHeight: '44px',
  paddingX: 0,
  paddingY: 2,
  border: 0,
  backgroundColor: 'transparent',
  color: 'rgba(6, 3, 15, 0.56)',
  fontSize: '17px',
  lineHeight: '26px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  WebkitTapHighlightColor: 'transparent',
  transition: `color .3s ${EASE}`,
  _after: {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '-1px',
    height: '2px',
    backgroundColor: 'surface.500',
    transform: 'scaleX(0)',
    transition: `transform .35s ${EASE}`,
  },
  '&:not([aria-selected="true"]):hover': { color: 'surface.500' },
  '&[aria-selected="true"]': { color: 'surface.500', _after: { transform: 'scaleX(1)' } },
  _focusVisible: { outline: '2px solid', outlineColor: 'surface.500', outlineOffset: '2px' },
});

const pickLabelClass = css({
  display: 'block',
  marginTop: { base: 8, md: 10 },
  marginBottom: 1,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'rgba(6, 3, 15, 0.64)',
});
const agentListClass = css({ display: 'grid' });
const agentTabClass = css({
  appearance: 'none',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  width: '100%',
  minHeight: '64px',
  paddingLeft: 4,
  paddingRight: 1,
  paddingY: 3,
  border: 0,
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: LINE,
  backgroundColor: 'transparent',
  color: 'rgba(6, 3, 15, 0.64)',
  fontFamily: 'heading',
  fontSize: { base: '17px', md: '18px' },
  lineHeight: '26px',
  textAlign: 'left',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  transition: `color .3s ${EASE}, background-color .3s ${EASE}`,
  '& > span:first-child': {
    flex: 'none',
    display: 'grid',
    placeItems: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: 'rgba(6, 3, 15, 0.05)',
  },
  '& > span:nth-child(2)': { flex: 1, minWidth: 0 },
  '& svg': { width: '20px', height: '20px' },
  '& > svg:last-child': {
    flex: 'none',
    width: '18px',
    height: '18px',
    color: 'rgba(6, 3, 15, 0.4)',
    transition: `transform .3s ${EASE}, color .3s ${EASE}`,
  },
  '&:not([aria-selected="true"]):hover': {
    color: 'surface.500',
    backgroundColor: 'rgba(6, 3, 15, 0.03)',
    '& > svg:last-child': { transform: 'translateX(4px)', color: 'surface.500' },
  },
  '&[aria-selected="true"]': {
    cursor: 'default',
    color: 'surface.500',
    '& > svg:last-child': { color: 'surface.500' },
  },
  // The open row's dark edge.
  _before: {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 3,
    bottom: 3,
    width: '2px',
    borderRadius: '2px',
    backgroundColor: 'surface.500',
    opacity: 0,
    transition: 'opacity .3s',
  },
  '&[aria-selected="true"]::before': { opacity: 1 },
  _focusVisible: { outline: '2px solid', outlineColor: 'surface.500', outlineOffset: '2px' },
});

const Chevron = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const moveKey = (key: string, current: number, count: number, prev: string, next: string) => {
  if (key === prev) return (current - 1 + count) % count;
  if (key === next) return (current + 1) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
};

/**
 * "Sample AI agents" of SH Business IQ in two columns: on the left the heading, the industry pills
 * and that industry's agents as tabs; on the right one card, the open agent's. Every card is in the
 * SSR HTML (`hidden` ones too). After the first switch the root gets `data-switched`, so a card
 * that turns visible replays its entry (CSS, in the section).
 */
export const AgentBrowser = ({ intro, industries, cards, industriesLabel, pickLabel }: Props) => {
  const id = useId();
  const industryRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const agentRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [industry, setIndustry] = useState(0);
  const [agent, setAgent] = useState(0);
  const [switched, setSwitched] = useState(false);

  const openIndustry = (next: number, focus = false) => {
    if (focus) industryRefs.current[next]?.focus();
    if (next === industry) return;
    setIndustry(next);
    setAgent(0);
    setSwitched(true);
  };
  const openAgent = (next: number, focus = false) => {
    if (focus) agentRefs.current[next]?.focus();
    if (next === agent) return;
    setAgent(next);
    setSwitched(true);
  };

  const onIndustryKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const next = moveKey(event.key, industry, industries.length, 'ArrowLeft', 'ArrowRight');
    if (next === null) return;
    event.preventDefault();
    openIndustry(next, true);
  };
  const onAgentKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const count = industries[industry]?.agents.length ?? 0;
    const next = moveKey(event.key, agent, count, 'ArrowUp', 'ArrowDown');
    if (next === null) return;
    event.preventDefault();
    openAgent(next, true);
  };

  return (
    <div className={rootClass} data-switched={switched ? '' : undefined}>
      <div>
        {intro}
        <div className={scrollerClass}>
          <div className={pillsClass} role="tablist" aria-label={industriesLabel}>
            {industries.map((item, i) => (
              <button
                key={item.name}
                ref={(node) => {
                  industryRefs.current[i] = node;
                }}
                type="button"
                role="tab"
                id={`${id}-industry-${i}`}
                aria-controls={`${id}-agents-${i}`}
                aria-selected={i === industry}
                tabIndex={i === industry ? 0 : -1}
                className={pillClass}
                onClick={() => openIndustry(i)}
                onKeyDown={onIndustryKey}
              >
                {item.name}
              </button>
            ))}
          </div>
        </div>
        {industries.map((item, i) => (
          <div
            key={item.name}
            role="tabpanel"
            id={`${id}-agents-${i}`}
            aria-labelledby={`${id}-industry-${i}`}
            hidden={i !== industry}
          >
            <span className={pickLabelClass}>{pickLabel}</span>
            <div className={agentListClass} role="tablist" aria-label={item.name} aria-orientation="vertical">
              {item.agents.map((entry, a) => (
                <button
                  key={entry.name}
                  ref={(node) => {
                    if (i === industry) agentRefs.current[a] = node;
                  }}
                  type="button"
                  role="tab"
                  id={`${id}-agent-${i}-${a}`}
                  aria-controls={`${id}-card-${i}-${a}`}
                  aria-selected={i === industry && a === agent}
                  tabIndex={i === industry && a === agent ? 0 : -1}
                  className={agentTabClass}
                  onClick={() => openAgent(a)}
                  onKeyDown={onAgentKey}
                >
                  <span aria-hidden="true">{entry.icon}</span>
                  <span>{entry.name}</span>
                  <Chevron />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div>
        {cards.map((row, i) =>
          row.map((card, a) => (
            <div
              key={`${i}-${a}`}
              role="tabpanel"
              id={`${id}-card-${i}-${a}`}
              aria-labelledby={`${id}-agent-${i}-${a}`}
              tabIndex={0}
              hidden={i !== industry || a !== agent}
            >
              {card}
            </div>
          )),
        )}
      </div>
    </div>
  );
};
